import React, { useState } from 'react';
import {
  ShieldAlert,
  ShieldCheck,
  Globe,
  Sliders,
  CheckCircle2,
  Lock,
  RotateCcw,
  FileText,
  Download,
  AlertCircle
} from 'lucide-react';
import { monetizationEngine } from '../../services/monetizationEngine';

export const ConsentPrivacyEngine: React.FC = () => {
  const [consentStatus, setConsentStatus] = useState(monetizationEngine.getConsentStatus());
  const [exportNotice, setExportNotice] = useState<string | null>(null);

  const handleTogglePreference = (key: 'analytics' | 'personalization' | 'advertising') => {
    const updated = {
      ...consentStatus.preferences,
      [key]: !consentStatus.preferences[key],
    };
    monetizationEngine.updateConsent(updated);
    setConsentStatus(monetizationEngine.getConsentStatus());
  };

  const handleRevokeAll = () => {
    monetizationEngine.updateConsent({
      essential: true,
      analytics: false,
      personalization: false,
      advertising: false,
    });
    setConsentStatus(monetizationEngine.getConsentStatus());
  };

  const handleGrantAll = () => {
    monetizationEngine.updateConsent({
      essential: true,
      analytics: true,
      personalization: true,
      advertising: true,
    });
    setConsentStatus(monetizationEngine.getConsentStatus());
  };

  const handleExportAuditLogs = () => {
    const data = JSON.stringify(consentStatus, null, 2);
    const blob = new Blob([data], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `yamoopay_consent_audit_${Date.now()}.json`;
    a.click();
    setExportNotice('Consent audit record exported successfully.');
    setTimeout(() => setExportNotice(null), 3000);
  };

  return (
    <div className="space-y-8 pb-12">
      {/* Banner */}
      <div className="bg-slate-900 text-white rounded-2xl p-8 shadow-xl space-y-4">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-purple-500/20 text-purple-300 text-xs font-bold border border-purple-500/30">
          <ShieldAlert className="w-3.5 h-3.5" />
          Google-Certified CMP & Privacy Signal Engine
        </div>
        <h1 className="text-3xl font-extrabold tracking-tight">Consent Management & Regional Privacy Signals</h1>
        <p className="text-slate-300 text-sm max-w-2xl leading-relaxed">
          Integrated IAB Europe TCF v2.2 Framework for EEA/UK/CH and Global Privacy Platform (GPP) for US State Privacy Laws.
        </p>
      </div>

      {/* Regional Privacy Coverage Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm space-y-3">
          <div className="flex items-center justify-between border-b border-slate-100 pb-2">
            <h3 className="font-bold text-slate-900 text-sm">EEA / UK / Switzerland</h3>
            <span className="px-2 py-0.5 rounded bg-purple-100 text-purple-800 text-[10px] font-bold">
              TCF v2.2
            </span>
          </div>
          <p className="text-slate-600 text-xs leading-relaxed">
            Google-certified TCF CMP active. Requires explicit affirmative consent prior to loading personalized ad scripts.
          </p>
          <div className="text-[11px] text-emerald-700 font-bold flex items-center gap-1">
            <CheckCircle2 className="w-3.5 h-3.5" /> Certified Google CMP Active
          </div>
        </div>

        <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm space-y-3">
          <div className="flex items-center justify-between border-b border-slate-100 pb-2">
            <h3 className="font-bold text-slate-900 text-sm">United States (All States)</h3>
            <span className="px-2 py-0.5 rounded bg-blue-100 text-blue-800 text-[10px] font-bold">
              US GPP
            </span>
          </div>
          <p className="text-slate-600 text-xs leading-relaxed">
            Global Privacy Platform signals (California CCPA/CPRA, Virginia VCDPA, Colorado CPA, etc.) supporting Opt-Out mechanisms.
          </p>
          <div className="text-[11px] text-emerald-700 font-bold flex items-center gap-1">
            <CheckCircle2 className="w-3.5 h-3.5" /> Do Not Sell/Share Supported
          </div>
        </div>

        <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm space-y-3">
          <div className="flex items-center justify-between border-b border-slate-100 pb-2">
            <h3 className="font-bold text-slate-900 text-sm">Rest of World / Global Fallback</h3>
            <span className="px-2 py-0.5 rounded bg-slate-100 text-slate-800 text-[10px] font-bold">
              Privacy First
            </span>
          </div>
          <p className="text-slate-600 text-xs leading-relaxed">
            Fail-closed default. Non-essential ad requests fallback to contextual non-personalized formats when region is uncertain.
          </p>
          <div className="text-[11px] text-emerald-700 font-bold flex items-center gap-1">
            <CheckCircle2 className="w-3.5 h-3.5" /> Fail-Closed Security Active
          </div>
        </div>
      </div>

      {/* Interactive Consent Preference Center */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 sm:p-8 space-y-6">
        <div className="flex flex-wrap items-center justify-between gap-4 border-b border-slate-100 pb-4">
          <div>
            <h2 className="text-xl font-bold text-slate-900">User Privacy & Cookie Preference Controls</h2>
            <p className="text-slate-500 text-xs mt-1">
              Test consent choices in real-time. Rejecting advertising or analytics consent never restricts access to financial calculators.
            </p>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={handleGrantAll}
              className="px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-xs font-bold transition-all cursor-pointer"
            >
              Accept All
            </button>
            <button
              onClick={handleRevokeAll}
              className="px-3 py-1.5 bg-slate-200 hover:bg-slate-300 text-slate-800 rounded-lg text-xs font-bold transition-all cursor-pointer flex items-center gap-1"
            >
              <RotateCcw className="w-3 h-3" /> Revoke Non-Essential
            </button>
          </div>
        </div>

        {/* Categories Switches */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Essential */}
          <div className="p-4 bg-slate-50 rounded-xl border border-slate-200/80 space-y-2">
            <div className="flex items-center justify-between">
              <span className="font-bold text-slate-900 text-sm">1. Essential Operations</span>
              <span className="px-2 py-0.5 rounded bg-emerald-100 text-emerald-800 text-[10px] font-extrabold">
                REQUIRED
              </span>
            </div>
            <p className="text-slate-600 text-xs leading-relaxed">
              Required for security, session state, and core financial calculation functions. Cannot be turned off.
            </p>
          </div>

          {/* Analytics */}
          <div className="p-4 bg-slate-50 rounded-xl border border-slate-200/80 space-y-2">
            <div className="flex items-center justify-between">
              <span className="font-bold text-slate-900 text-sm">2. Product Analytics</span>
              <button
                onClick={() => handleTogglePreference('analytics')}
                className={`px-3 py-1 rounded-full text-xs font-bold transition-all cursor-pointer ${
                  consentStatus.preferences.analytics
                    ? 'bg-emerald-600 text-white'
                    : 'bg-slate-300 text-slate-700'
                }`}
              >
                {consentStatus.preferences.analytics ? 'GRANTED' : 'REVOKED'}
              </button>
            </div>
            <p className="text-slate-600 text-xs leading-relaxed">
              Anonymized aggregate performance measuring tool completion rates and page latency.
            </p>
          </div>

          {/* Personalization */}
          <div className="p-4 bg-slate-50 rounded-xl border border-slate-200/80 space-y-2">
            <div className="flex items-center justify-between">
              <span className="font-bold text-slate-900 text-sm">3. First-Party Personalization</span>
              <button
                onClick={() => handleTogglePreference('personalization')}
                className={`px-3 py-1 rounded-full text-xs font-bold transition-all cursor-pointer ${
                  consentStatus.preferences.personalization
                    ? 'bg-emerald-600 text-white'
                    : 'bg-slate-300 text-slate-700'
                }`}
              >
                {consentStatus.preferences.personalization ? 'GRANTED' : 'REVOKED'}
              </button>
            </div>
            <p className="text-slate-600 text-xs leading-relaxed">
              Saves default currency pairs and recently calculated salary scenarios locally in browser.
            </p>
          </div>

          {/* Advertising */}
          <div className="p-4 bg-slate-50 rounded-xl border border-slate-200/80 space-y-2">
            <div className="flex items-center justify-between">
              <span className="font-bold text-slate-900 text-sm">4. Programmatic Display Ads</span>
              <button
                onClick={() => handleTogglePreference('advertising')}
                className={`px-3 py-1 rounded-full text-xs font-bold transition-all cursor-pointer ${
                  consentStatus.preferences.advertising
                    ? 'bg-emerald-600 text-white'
                    : 'bg-slate-300 text-slate-700'
                }`}
              >
                {consentStatus.preferences.advertising ? 'GRANTED' : 'REVOKED'}
              </button>
            </div>
            <p className="text-slate-600 text-xs leading-relaxed">
              Allows Google AdSense & Ezoic to serve personalized programmatic advertisements.
            </p>
          </div>
        </div>

        {/* Audit Export Trigger */}
        <div className="pt-4 border-t border-slate-100 flex flex-wrap items-center justify-between gap-4">
          <div className="text-xs text-slate-500">
            Current Consent State: <strong className="text-slate-800">{consentStatus.consentGiven ? 'Affirmative Consent' : 'Non-Personalized Only'}</strong>
          </div>

          <button
            onClick={handleExportAuditLogs}
            className="px-4 py-2 bg-slate-900 hover:bg-slate-800 text-white rounded-lg text-xs font-bold transition-all flex items-center gap-1.5 cursor-pointer"
          >
            <Download className="w-3.5 h-3.5 text-emerald-400" /> Export Consent Audit Record
          </button>
        </div>

        {exportNotice && (
          <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-lg text-xs font-bold text-emerald-800 flex items-center gap-2">
            <CheckCircle2 className="w-4 h-4 text-emerald-600" /> {exportNotice}
          </div>
        )}
      </div>
    </div>
  );
};
